/** Marker used to detect whether the safety protocol is already present. */
export const NON_DESTRUCTIVE_PROTOCOL_MARKER = 'NON-DESTRUCTIVE CONFIG PROTOCOL';

/** Marker for the mandatory system integrity pre-flight block. */
export const SYSTEM_INTEGRITY_PROTOCOL_MARKER =
  '[SYSTEM_INTEGRITY_PROTOCOL - MANDATORY PRE-FLIGHT CHECK]';

/**
 * Master integrity protocol — appended automatically to every agent system prompt.
 * Source of truth: docs/AI_OPERATING_PROCEDURES/
 */
export const SYSTEM_INTEGRITY_PROTOCOL = `
[SYSTEM_INTEGRITY_PROTOCOL - MANDATORY PRE-FLIGHT CHECK]
BEFORE generating any code or proposing a patch, you MUST comply with the following:
1. READ THE DOCS: If you have a file-reading tool, immediately read 'docs/AI_OPERATING_PROCEDURES/NON_DESTRUCTIVE_PATCHING.md' and 'ARCHITECTURE_GUIDE.md'.
2. CHECK YOUR PATCH: Prefer patch_file (exact find/replace). Does your proposed code rewrite an entire large file when a surgical replace is possible? If YES, CANCEL and use patch_file instead.
3. NO TRUNCATION STUBS: Never emit '// ... existing code ...', '/* rest of file */', or similar placeholders. Incomplete files will be rejected by tools and blocked by GitOps CI.
4. VERIFY IMPORTS: Does your import path match the actual file structure in 'src/'? If you cannot verify the file exists, DO NOT invent the import. Specifically, NEVER import 'useAuth'.
5. PENALTY: Violating these rules will crash the production build. The Factory will consider the patch DESTRUCTIVE and flag your execution for immediate human override.
`;

/**
 * Default safety protocol baked into every new AI agent system prompt.
 * Governs surgical patching for sensitive configuration files.
 */
export const DEFAULT_NON_DESTRUCTIVE_PROTOCOL = `
- THE NON-DESTRUCTIVE CONFIG PROTOCOL: When editing complex configuration or security files (specifically firestore.rules, firebase.json, or .env), you are strictly forbidden from rewriting or overwriting the entire file. You MUST use surgical patch_file edits.
* Rule 1: Never delete existing helper functions or unrelated match blocks.
* Rule 2: Change ONLY the specific block you are adding or modifying via patch_file find/replace.
* Rule 3: NEVER use "// ... existing code ..." or any truncation placeholder — those corrupt the repository when committed.
* Rule 4: Provide enough unique surrounding lines in 'find' so the exact insertion point is unmistakable.
`.trim();

/** Marker for critical patching standards block. */
export const CRITICAL_PATCHING_STANDARDS_MARKER =
  '# CRITICAL PATCHING AND DEVELOPMENT STANDARDS';

/**
 * Layer 3 psychological guardrails for coding agents.
 */
export const CRITICAL_PATCHING_STANDARDS = `
# CRITICAL PATCHING AND DEVELOPMENT STANDARDS

1. NO TRUNCATION PLACEHOLDERS: You are strictly forbidden from using placeholders like \`// ... existing code ...\`, \`/* rest of file */\`, or \`...\` when generating or patching files. Any file you write or patch must be syntactically complete, valid, and functional.
2. PREFER SURGICAL PATCHING: When editing existing files, you MUST use the \`patch_file\` tool to target only the specific blocks that require modifications. Do not attempt to rewrite entire files if a surgical replace is possible.
3. VERIFY MATCHES: When defining the \`find\` block for a patch, provide enough unique lines (including functions, imports, or variable declarations) to ensure the system can locate the exact line range without ambiguity.
4. SANITY CHECK: If you violate these constraints, the GitOps CI pipeline will automatically reject your code, and your workspace session will flag a failure.
5. CODE REVIEW REJECTIONS: See ### HANDLING REJECTIONS below. Rejected patches are discarded — never yield waiting for them to merge.
`.trim();

/** Marker for the rejection-workflow directive. */
export const HANDLING_REJECTIONS_MARKER = '### HANDLING REJECTIONS';

/**
 * Universal rejection workflow — Gemini and Anthropic workers.
 */
export const HANDLING_REJECTIONS_PROTOCOL = `
### HANDLING REJECTIONS
If you receive a message containing [AI_SYSTEM_REJECTION] or [JUDGE AGENT CODE COURT], it means your previous patch failed QA and has been discarded. DO NOT yield and DO NOT wait for the old patch to be merged. You MUST immediately use the patch_file tool to generate a brand NEW patch that directly addresses the feedback provided. Treat rejections as immediate action items.
`.trim();

/**
 * Roles that touch repository / config files and must carry the protocol.
 * @param {string} roleTitle
 * @returns {boolean}
 */
export function isCodeCapableAgentRole(roleTitle) {
  if (typeof roleTitle !== 'string' || !roleTitle.trim()) return false;
  return /tech\s*lead|engineer|developer|architect|devops|full\s*stack|qa\s*automation/i.test(
    roleTitle.trim(),
  );
}

/**
 * @param {string} text
 * @returns {boolean}
 */
export function protocolAlreadyPresent(text) {
  return typeof text === 'string' && text.includes(NON_DESTRUCTIVE_PROTOCOL_MARKER);
}

/**
 * Ensure the non-destructive protocol is present in a system prompt.
 * @param {string} systemPrompt
 * @param {string} [roleTitle]
 * @param {{ codeRolesOnly?: boolean }} [options]
 * @returns {string}
 */
export function ensureNonDestructiveProtocol(systemPrompt, roleTitle = '', options = {}) {
  const prompt = typeof systemPrompt === 'string' ? systemPrompt.trim() : '';
  if (protocolAlreadyPresent(prompt)) return prompt;
  if (options.codeRolesOnly && !isCodeCapableAgentRole(roleTitle)) return prompt;
  if (!prompt) return DEFAULT_NON_DESTRUCTIVE_PROTOCOL;
  return `${prompt}\n\n${DEFAULT_NON_DESTRUCTIVE_PROTOCOL}`;
}

/**
 * @param {string} systemPrompt
 * @returns {string}
 */
export function stripSystemIntegrityProtocol(systemPrompt) {
  if (typeof systemPrompt !== 'string' || !systemPrompt.includes(SYSTEM_INTEGRITY_PROTOCOL_MARKER)) {
    return systemPrompt;
  }
  const idx = systemPrompt.indexOf(SYSTEM_INTEGRITY_PROTOCOL_MARKER);
  return systemPrompt.slice(0, idx).trimEnd();
}

/**
 * Append the master integrity protocol if not already present.
 * @param {string} systemPrompt
 * @returns {string}
 */
export function ensureSystemIntegrityProtocol(systemPrompt) {
  const prompt = stripSystemIntegrityProtocol(
    typeof systemPrompt === 'string' ? systemPrompt : '',
  ).trimEnd();
  const block = SYSTEM_INTEGRITY_PROTOCOL.trim();
  if (!prompt) return block;
  return `${prompt}\n\n${block}`;
}

/**
 * @param {string} systemPrompt
 * @param {{ codeRolesOnly?: boolean, roleTitle?: string }} [options]
 * @returns {string}
 */
export function ensureCriticalPatchingStandards(systemPrompt, options = {}) {
  const prompt = typeof systemPrompt === 'string' ? systemPrompt.trim() : '';
  if (prompt.includes(CRITICAL_PATCHING_STANDARDS_MARKER)) return prompt;
  if (options.codeRolesOnly && !isCodeCapableAgentRole(options.roleTitle || '')) {
    return prompt;
  }
  const block = CRITICAL_PATCHING_STANDARDS.trim();
  if (!prompt) return block;
  return `${prompt}\n\n${block}`;
}

/**
 * Ensure rejection-workflow directive is present (coding workers).
 * @param {string} systemPrompt
 * @param {{ codeRolesOnly?: boolean, roleTitle?: string }} [options]
 * @returns {string}
 */
export function ensureRejectionHandlingProtocol(systemPrompt, options = {}) {
  const prompt = typeof systemPrompt === 'string' ? systemPrompt.trim() : '';
  if (prompt.includes(HANDLING_REJECTIONS_MARKER)) return prompt;
  if (options.codeRolesOnly && !isCodeCapableAgentRole(options.roleTitle || '')) {
    return prompt;
  }
  const block = HANDLING_REJECTIONS_PROTOCOL.trim();
  if (!prompt) return block;
  return `${prompt}\n\n${block}`;
}

export const YIELD_PROTOCOL_MARKER = 'THE YIELD PROTOCOL';

/** Injected for agents with create_ticket / delegation capability. */
export const YIELD_PROTOCOL = [
  '- THE YIELD PROTOCOL: When you have successfully delegated tasks to other AI workers (e.g., via create_ticket) and cannot proceed until they finish, DO NOT request human review.',
  'Instead, you MUST use the update_ticket_status tool on your own ticket.',
  'Set your status to `blocked` or `waiting_on_dependencies`, and leave a comment stating which ticket IDs you are waiting on.',
  'This will safely yield your execution.',
  'IMPORTANT: Do NOT yield after [AI_SYSTEM_REJECTION] or [JUDGE AGENT CODE COURT] — rejected patches are discarded; fix and resubmit immediately with patch_file.',
].join(' ');

/**
 * @param {string} systemInstruction
 * @param {{ includeYield?: boolean }} [options]
 * @returns {string}
 */
export function appendYieldProtocol(systemInstruction, options = {}) {
  if (!options.includeYield) {
    return typeof systemInstruction === 'string' ? systemInstruction.trim() : '';
  }
  const base = typeof systemInstruction === 'string' ? systemInstruction.trim() : '';
  if (base.includes(YIELD_PROTOCOL_MARKER)) return base;
  if (!base) return YIELD_PROTOCOL;
  return `${base}\n\n${YIELD_PROTOCOL}`;
}
